import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import * as Cesium from 'cesium';
import { computeMetersPerPixelAt, computeScaleBar } from '../utils/printScale';
// Furniture that sits ON the map window (Gaia-style): a north compass top-right
// and a graphic scale bar bottom-right, each in a soft pill so they read over
// imagery. Fills its parent (which is positioned to the map-window rect). The
// exporter renders this as a separate layer ON TOP of the captured map image.
const INK = '#2f3027';
const PILL_BG = 'rgba(255, 255, 255, 0.88)';
const PILL_BORDER = 'rgba(62, 63, 54, 0.35)';
const NADIR_TOLERANCE_DEG = 1;
const NorthArrow = ({ headingDeg }) => (_jsx("div", { style: { transform: `rotate(${-headingDeg}deg)`, width: 20, height: 25 }, children: _jsxs("svg", { width: "20", height: "25", viewBox: "0 0 30 38", children: [_jsx("text", { x: "15", y: "9", textAnchor: "middle", fontSize: "11", fontWeight: "700", fill: INK, children: "N" }), _jsx("polygon", { points: "15,12 8,32 15,27 22,32", fill: INK })] }) }));
const PrintMapOverlay = ({ viewer, windowCenter, scaleSystem }) => {
    const [headingDeg, setHeadingDeg] = useState(0);
    const [pitchDeg, setPitchDeg] = useState(-90);
    const [mpp, setMpp] = useState(null);
    const lastRef = useRef(0);
    // Track heading + scale live as the user composes (throttled).
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        const update = () => {
            const now = performance.now();
            if (now - lastRef.current < 150)
                return;
            lastRef.current = now;
            setHeadingDeg(Cesium.Math.toDegrees(viewer.camera.heading));
            setPitchDeg(Cesium.Math.toDegrees(viewer.camera.pitch));
            setMpp(computeMetersPerPixelAt(viewer, windowCenter.x, windowCenter.y));
        };
        update();
        const remove = viewer.scene.postRender.addEventListener(update);
        return () => remove();
    }, [viewer, windowCenter.x, windowCenter.y]);
    // A graphic scale bar is only valid looking straight down.
    const isNadir = Math.abs(pitchDeg - -90) < NADIR_TOLERANCE_DEG;
    // Compact target so the on-map pill stays small (the title-block bar used 150).
    const scaleBar = isNadir ? computeScaleBar(mpp, scaleSystem, 80) : null;
    return (_jsxs("div", { style: {
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            fontFamily: 'Helvetica, Arial, sans-serif',
        }, children: [_jsx("div", { style: {
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    width: 32,
                    height: 32,
                    background: PILL_BG,
                    border: `1px solid ${PILL_BORDER}`,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                }, children: _jsx(NorthArrow, { headingDeg: headingDeg }) }), scaleBar ? (_jsxs("div", { style: {
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    background: PILL_BG,
                    border: `1px solid ${PILL_BORDER}`,
                    borderRadius: 8,
                    // Extra bottom padding absorbs html2canvas's residual baseline
                    // fudge so the label can't sit on (or under) the pill edge in the
                    // exported PDF. See the line-height note in PrintTitleBlock.
                    padding: '3px 8px 4px',
                    textAlign: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                }, children: [_jsx("div", { style: {
                            display: 'inline-block',
                            position: 'relative',
                            width: Math.round(scaleBar.widthPx),
                            height: 5,
                            borderLeft: `2px solid ${INK}`,
                            borderRight: `2px solid ${INK}`,
                            borderBottom: `2px solid ${INK}`,
                        }, children: _jsx("div", { style: {
                                position: 'absolute',
                                left: '50%',
                                bottom: 0,
                                height: 5,
                                borderLeft: `2px solid ${INK}`,
                                transform: 'translateX(-1px)',
                            } }) }), _jsx("div", { style: { fontSize: 10, color: INK, marginTop: 1, lineHeight: 1.5, textAlign: 'center', whiteSpace: 'nowrap' }, children: scaleBar.label })] })) : null] }));
};
export default PrintMapOverlay;
